/**
 * Recognises fetches aimed at the third-party marketing/referral beacons the
 * app loads in `app/layout.tsx`, so the global fetch interceptor can answer
 * their network failures with a synthetic response instead of letting an
 * unhandled rejection escape.
 *
 * WHY THIS EXISTS. `components/auth/email-verification-gate-modal.tsx` patches
 * `window.fetch` at the app root (the 403 `email_verification_required`
 * backstop). Every fetch on the page — ours AND every third-party script's —
 * now runs through an `await original(...)` inside OUR module. When the Affonso
 * referral pixel (`cdn.affonso.io/js/pixel.min.js`, which POSTs the `?ref=`
 * visit to `api.affonso.io`) fails — ad blocker, offline, affiliate-service
 * outage — the resulting `TypeError: Failed to fetch` carries our wrapper on
 * its stack, the pixel doesn't catch it, and PostHog Error Tracking files the
 * rejection as a Panda shell crash inside the email verification gate
 * (issue 01a03386-9efc-7fe1-a4a8-e15cd5d5ae16, first seen 2026-08-24 on
 * `/?ref=…&source=build_earn_badge`).
 *
 * THE HOST CRITERION. A host belongs here only when BOTH hold:
 *
 *   1. It is a fire-and-forget analytics/attribution beacon — Panda code never
 *      reads its response, so a failure cannot change what the user sees.
 *   2. Panda's own client code never fetches it. Every request to it comes
 *      from a vendor script we don't control and can't wrap in a catch.
 *
 * That is deliberately narrow. Supabase, Stripe and our own API are all
 * cross-origin too, and swallowing THEIR failures would hide real bugs — so
 * matching is host-based and exact (host, or a subdomain of it), never a
 * substring: `evil.com/api.affonso.io` and `affonso.io.evil.com` do not match.
 *
 * Pure and dependency-free so it is trivially unit-testable and reusable on any
 * fetch path. Mirrors the other noise classifiers in this directory.
 */

/**
 * Registrable domains of the beacons loaded by `app/layout.tsx`. Subdomains are
 * covered (`api.affonso.io`, `www.googletagmanager.com`, …).
 */
export const THIRD_PARTY_BEACON_HOSTS = [
  // Affonso — referral/affiliate pixel. The host in the reported issue.
  "affonso.io",
  // Google Tag Manager / Analytics.
  "googletagmanager.com",
  "google-analytics.com",
  "analytics.google.com",
  // Meta pixel.
  "facebook.com",
  "facebook.net",
] as const;

/** Marker header on the synthetic response, so callers/tests can spot it. */
export const THIRD_PARTY_BEACON_FAILED_HEADER = "x-panda-beacon-failed";

/** Pulls the URL string out of any `fetch` first argument. */
function requestUrlOf(input: unknown): string | null {
  if (typeof input === "string") return input;
  if (typeof URL !== "undefined" && input instanceof URL) return input.href;
  // Duck-typed rather than `instanceof Request`: polyfilled/cross-realm
  // Request objects fail the instanceof check but always carry `.url`.
  const url = (input as { url?: unknown } | null | undefined)?.url;
  return typeof url === "string" ? url : null;
}

/**
 * The beacon host this request targets, or `null` when it's anything else —
 * including every same-origin and relative URL, and every host we depend on.
 */
export function thirdPartyBeaconHost(input: unknown): string | null {
  try {
    const raw = requestUrlOf(input);
    if (!raw) return null;
    const base =
      typeof location !== "undefined" && location.href ? location.href : undefined;
    const host = new URL(raw, base).hostname.toLowerCase();
    const match = THIRD_PARTY_BEACON_HOSTS.find(
      (h) => host === h || host.endsWith(`.${h}`),
    );
    return match ? host : null;
  } catch {
    // Unparseable URL — treat as ours, i.e. don't swallow anything.
    return null;
  }
}

/**
 * The safe fallback handed to a beacon whose request failed: an OK response
 * with an empty JSON object — "delivered, nothing to apply".
 *
 * WHY NOT AN HONEST 5xx. It has to be a status the vendor treats as success,
 * because the convention in these scripts is to throw on a non-2xx. Affonso's
 * `pagevisitor()` is exactly that, and it is called fire-and-forget with no
 * `.catch()` — so a `503` merely swaps the unhandled `TypeError: Failed to
 * fetch` for an unhandled `Error: API Error: 503`, which is not a fix:
 *
 *     const n = await fetch(`${e}/track`, …);
 *     if (!n.ok) throw new Error(`API Error: ${n.status}`);
 *     const e = await n.json();
 *     if (e.cid) { …cookie write, URL decoration, `affonso_referral_ready`… }
 *
 * An empty object clears both hurdles and then stops dead at the `e.cid`
 * guard: no cookie, no rewritten landing URL, no event, nothing observable.
 * `{}` is the shape that says "no referral resolved", which — the request
 * having never reached the network — is the truth. And it costs the product
 * nothing: Panda reads `?ref=` itself into `panda_ref` (app/home-client.tsx)
 * and attributes the signup through `/api/referrals/track-signup` server-side,
 * so referral credit never depended on this response. The failure is not hidden;
 * it is reported as a `third_party_beacon_failed` product event instead of a
 * frontend crash, and the marker header keeps it identifiable in the response.
 */
export function thirdPartyBeaconFailureResponse(): Response {
  return new Response("{}", {
    status: 200,
    statusText: "Third-party beacon unreachable",
    headers: {
      "content-type": "application/json",
      [THIRD_PARTY_BEACON_FAILED_HEADER]: "1",
    },
  });
}
